// src/pages/Settings.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FiUser, FiMail, FiBell, FiShield, FiCreditCard, 
  FiGlobe, FiSettings, FiDatabase, FiChevronRight,
  FiArrowLeft, FiCheck, FiAlertCircle
} from 'react-icons/fi';
import { X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, organizationProfile, updateUserProfile, updateOrganizationProfile } = useAuth();
  
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  
  // Form state for user data
  const [userData, setUserData] = useState({
    displayName: '',
    email: '',
    jobTitle: '',
    phone: '',
    timezone: ''
  });
  
  // Form state for organization data
  const [orgData, setOrgData] = useState({
    organizationName: '',
    industry: '',
    website: '',
    phone: ''
  });

  // Load initial data from context
  useEffect(() => {
    if (userProfile) {
      setUserData({
        displayName: userProfile.displayName || '',
        email: userProfile.email || '',
        jobTitle: userProfile.jobTitle || '',
        phone: userProfile.phone || '',
        timezone: userProfile.settings?.preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }
  }, [userProfile]);

  useEffect(() => {
    if (organizationProfile) {
      setOrgData({
        organizationName: organizationProfile.name || '',
        industry: organizationProfile.industry || '',
        website: organizationProfile.website || '',
        phone: organizationProfile.phone || ''
      });
    } else if (userProfile) {
      // Fallback to user profile if organization profile not loaded
      setOrgData({
        organizationName: userProfile.organizationName || '',
        industry: userProfile.industry || '',
        website: userProfile.website || '',
        phone: userProfile.phone || ''
      });
    }
  }, [organizationProfile, userProfile]);

  // Handle user data changes
  const handleUserDataChange = (field, value) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle organization data changes
  const handleOrgDataChange = (field, value) => {
    setOrgData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save changes
  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      setSaveMessage(null);

      // Update user profile
      await updateUserProfile({
        displayName: userData.displayName,
        jobTitle: userData.jobTitle,
        phone: userData.phone,
        settings: {
          ...userProfile?.settings,
          preferences: {
            ...userProfile?.settings?.preferences,
            timezone: userData.timezone
          }
        }
      });

      // Update organization profile if data changed
      if (organizationProfile) {
        await updateOrganizationProfile({
          name: orgData.organizationName,
          industry: orgData.industry,
          website: orgData.website,
          phone: orgData.phone
        });
      } else {
        // Update organization data in user profile if no separate org profile
        await updateUserProfile({
          organizationName: orgData.organizationName,
          industry: orgData.industry,
          website: orgData.website
        });
      }

      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({ type: 'error', text: error.message || 'Failed to save settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Cancel changes and return to dashboard
  const handleCancel = () => {
    navigate('/');
  };

  const settingsSections = [
    {
      id: 'general',
      name: 'General',
      icon: FiUser,
      description: 'Basic account and organization settings'
    },
    {
      id: 'email',
      name: 'Email Templates',
      icon: FiMail,
      description: 'Manage your email templates and automation',
      link: '/email/templates'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: FiBell,
      description: 'Configure your notification preferences'
    },
    {
      id: 'security',
      name: 'Security',
      icon: FiShield,
      description: 'Password, two-factor authentication, and security settings'
    },
    {
      id: 'billing',
      name: 'Billing',
      icon: FiCreditCard,
      description: 'Subscription, payments, and billing history'
    },
    {
      id: 'integrations',
      name: 'Integrations',
      icon: FiGlobe,
      description: 'Connect with external services and APIs'
    },
    {
      id: 'appearance',
      name: 'Appearance',
      icon: FiSettings,
      description: 'Customize the look and feel of your application'
    },
    {
      id: 'data',
      name: 'Data & Privacy',
      icon: FiDatabase,
      description: 'Export data, privacy settings, and data retention'
    }
  ];

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      {/* Success/Error Message */}
      {saveMessage && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${
          saveMessage.type === 'success' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          {saveMessage.type === 'success' ? (
            <FiCheck className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <FiAlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={`text-sm font-medium ${
            saveMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {saveMessage.text}
          </p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Organization Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name
            </label>
            <input
              type="text"
              value={orgData.organizationName}
              onChange={(e) => handleOrgDataChange('organizationName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your Organization"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Industry
            </label>
            <select 
              value={orgData.industry}
              onChange={(e) => handleOrgDataChange('industry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Industry</option>
              <option value="technology">Technology</option>
              <option value="healthcare">Healthcare</option>
              <option value="finance">Finance</option>
              <option value="education">Education</option>
              <option value="retail">Retail</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="consulting">Consulting</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={orgData.website}
              onChange={(e) => handleOrgDataChange('website', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://yourorganization.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={orgData.phone}
              onChange={(e) => handleOrgDataChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              type="text"
              value={userData.displayName}
              onChange={(e) => handleUserDataChange('displayName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={userData.email}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed"
              placeholder="john@yourorganization.com"
            />
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed here</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Title
            </label>
            <input
              type="text"
              value={userData.jobTitle}
              onChange={(e) => handleUserDataChange('jobTitle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Manager"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Zone
            </label>
            <select 
              value={userData.timezone}
              onChange={(e) => handleUserDataChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="America/Los_Angeles">UTC-08:00 (Pacific Time)</option>
              <option value="America/Denver">UTC-07:00 (Mountain Time)</option>
              <option value="America/Chicago">UTC-06:00 (Central Time)</option>
              <option value="America/New_York">UTC-05:00 (Eastern Time)</option>
              <option value="Europe/London">UTC+00:00 (GMT)</option>
              <option value="Europe/Paris">UTC+01:00 (CET)</option>
              <option value="Asia/Dubai">UTC+04:00 (GST)</option>
              <option value="Asia/Kolkata">UTC+05:30 (IST)</option>
              <option value="Asia/Shanghai">UTC+08:00 (CST)</option>
              <option value="Asia/Tokyo">UTC+09:00 (JST)</option>
              <option value="Australia/Sydney">UTC+10:00 (AEST)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-between pt-4 border-t border-gray-200">
        <button 
          onClick={handleCancel}
          disabled={loading}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <X className="mr-2 h-4 w-4" />
          Cancel
        </button>
        <button 
          onClick={handleSaveChanges}
          disabled={loading}
          className="inline-flex items-center justify-center px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <FiCheck className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderPlaceholderContent = (sectionName) => (
    <div>
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <FiSettings className="text-gray-400" size={24} />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{sectionName} Settings</h3>
        <p className="text-gray-500">Settings for {sectionName.toLowerCase()} will be available here.</p>
      </div>

      <div className="flex justify-start pt-6 border-t border-gray-200">
        <button 
          onClick={handleCancel}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <X className="mr-2 h-4 w-4" />
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          aria-label="Back to dashboard"
        >
          <FiArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage your account and application preferences</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-4">
          {/* Settings Navigation */}
          <div className="lg:col-span-1 bg-gray-50 border-r border-gray-200">
            <nav className="p-4 space-y-1">
              {settingsSections.map((section) => {
                if (section.link) {
                  return (
                    <Link
                      key={section.id}
                      to={section.link}
                      className="flex items-center justify-between p-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center">
                        <section.icon className="mr-3 h-5 w-5" />
                        {section.name}
                      </div>
                      <FiChevronRight size={16} />
                    </Link>
                  );
                }

                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveTab(section.id)}
                    className={`w-full flex items-center p-3 text-sm font-medium rounded-lg transition-colors text-left ${
                      activeTab === section.id
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <section.icon className="mr-3 h-5 w-5" />
                    {section.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3 p-4 sm:p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {settingsSections.find(s => s.id === activeTab)?.name}
              </h2>
              <p className="text-gray-600 mt-1 text-sm">
                {settingsSections.find(s => s.id === activeTab)?.description}
              </p>
            </div>

            {activeTab === 'general' ? renderGeneralSettings() : renderPlaceholderContent(
              settingsSections.find(s => s.id === activeTab)?.name
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;